import { apiService } from "./api";

// Answer Desk — the moderators' internal FAQ. Mirrors api/app/schemas/faq.py.

export type FaqTopic =
    | "PRODUCT_INFO"
    | "USAGE"
    | "HEALTH"
    | "DELIVERY"
    | "PAYMENT"
    | "RETURNS_ORDERS"
    | "AUTHENTICITY"
    | "STORE_INFO"
    | "QUICK_REPLY"
    | "OTHER";

export type FaqStatus = "REQUESTED" | "DRAFT" | "PUBLISHED" | "ARCHIVED";

export interface FaqTarget {
    faq_target_id: string;
    product_id: string | null;
    category_id: string | null;
    brand_id: string | null;
    include_subcategories: boolean;
    product_name: string | null;
    category_name: string | null;
    brand_name: string | null;
}

export interface FaqTargetInput {
    product_id?: string | null;
    category_id?: string | null;
    brand_id?: string | null;
    include_subcategories?: boolean;
}

export interface FaqImage {
    faq_image_id: string;
    faq_entry_id: string | null;
    original_url: string;
    large_url: string;
    medium_url: string;
    thumbnail_url: string;
    width: number;
    height: number;
    file_size: number;
    original_filename: string;
    display_order: number;
}

export type FaqImageVariant = "original" | "large" | "medium" | "thumbnail";

export interface FaqEntry {
    faq_entry_id: string;
    question: string;
    alt_phrasings: string[];
    answer: string | null;
    internal_note: string | null;
    topic: FaqTopic;
    status: FaqStatus;
    is_general: boolean;
    is_pinned: boolean;
    copy_count: number;
    last_copied_at: string | null;
    last_reviewed_at: string | null;
    flagged_at: string | null;
    flag_note: string | null;
    created_by_user_id: string | null;
    created_by_email: string | null;
    reviewed_by_email: string | null;
    flagged_by_email: string | null;
    is_mine: boolean;
    targets: FaqTarget[];
    images: FaqImage[];
    create_time: string;
    update_time: string;
}

export interface FaqEntryInput {
    question: string;
    alt_phrasings?: string[];
    answer?: string | null;
    internal_note?: string | null;
    topic?: FaqTopic;
    status?: FaqStatus;
    is_general?: boolean;
    is_pinned?: boolean;
    targets?: FaqTargetInput[];
    /** Ordered ids from uploadImage(); on update, the full new set. */
    image_ids?: string[];
}

export interface FaqMatchItem {
    faq_entry_id: string;
    level: number;
}

export type FaqMatchMap = Record<string, FaqMatchItem[]>;

export interface FaqReviewCounts {
    requested: number;
    drafts: number;
    flagged: number;
    needs_targets: number;
    stale: number;
    total_waiting: number;
}

const BASE = "/faq";

export const answerDeskService = {
    listEntries(includeArchived = false): Promise<FaqEntry[]> {
        return apiService.get<FaqEntry[]>(
            `${BASE}/entries?include_archived=${includeArchived}`,
        );
    },

    matchProducts(productIds: string[]): Promise<FaqMatchMap> {
        if (productIds.length === 0) return Promise.resolve({});
        return apiService.get<FaqMatchMap>(
            `${BASE}/match?product_ids=${encodeURIComponent(productIds.join(","))}`,
        );
    },

    getReviewCounts(): Promise<FaqReviewCounts> {
        return apiService.get<FaqReviewCounts>(`${BASE}/review-counts`);
    },

    createEntry(data: FaqEntryInput): Promise<FaqEntry> {
        return apiService.post<FaqEntry>(`${BASE}/entries`, data);
    },

    updateEntry(id: string, data: Partial<FaqEntryInput>): Promise<FaqEntry> {
        return apiService.patch<FaqEntry>(`${BASE}/entries/${id}`, data);
    },

    publish(id: string): Promise<FaqEntry> {
        return apiService.post<FaqEntry>(`${BASE}/entries/${id}/publish`);
    },

    markReviewed(id: string): Promise<FaqEntry> {
        return apiService.post<FaqEntry>(`${BASE}/entries/${id}/mark-reviewed`);
    },

    flag(id: string, note: string): Promise<FaqEntry> {
        return apiService.post<FaqEntry>(`${BASE}/entries/${id}/flag`, { note });
    },

    /** Usage counter — fire-and-forget, never blocks a copy. */
    recordCopy(id: string): void {
        apiService.post(`${BASE}/entries/${id}/copied`).catch(() => {
            /* non-critical */
        });
    },

    deleteEntry(id: string): Promise<{ deleted: boolean; archived: boolean }> {
        return apiService.delete(`${BASE}/entries/${id}`);
    },

    /** Uploads one image; it's attached when the answer is saved with its id. */
    uploadImage(file: File): Promise<FaqImage> {
        const formData = new FormData();
        formData.append("file", file);
        return apiService.post<FaqImage>(`${BASE}/images`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 60000, // resizing + storing four sizes can take a while
        });
    },

    /** Image bytes via the API (storage sends no CORS headers) — for copy/download. */
    getImageBlob(id: string, variant: FaqImageVariant = "large"): Promise<Blob> {
        return apiService.get<Blob>(`${BASE}/images/${id}/file?variant=${variant}`, {
            responseType: "blob",
            timeout: 60000,
        });
    },

    addStarterPack(): Promise<FaqEntry[]> {
        return apiService.post<FaqEntry[]>(`${BASE}/starter-pack`);
    },
};
